<template>
  <router-view />
</template>

<script>
export default {
  name: 'App',
  mounted() {
    if (localStorage.getItem('contraste_fort') === '1') {
      document.body.classList.add('contraste-fort')
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

:root {
  --fm-bg: #F8FAFC;
  --fm-surface: #FFFFFF;
  --fm-surface-soft: #F1F5F9;
  --fm-navy: #0D1B3D;
  --fm-navy-soft: #142852;
  --fm-accent: #1E6FDC;
  --fm-accent-soft: #E8F1FD;
  --fm-success: #22C55E;
  --fm-success-soft: #DCFCE7;
  --fm-warning: #F59E0B;
  --fm-warning-soft: #FEF3C7;
  --fm-danger: #EF4444;
  --fm-danger-soft: #FEE2E2;
  --fm-border: #E2E8F0;
  --fm-text: #0F172A;
  --fm-muted: #64748B;
  --fm-card-radius: 18px;
  --fm-control-radius: 12px;
  --fm-shadow-soft: 0 16px 45px rgba(15, 23, 42, 0.07);
  --fm-shadow-subtle: 0 8px 24px rgba(15, 23, 42, 0.05);
  --fm-transition: 0.2s ease;
}

* { margin: 0; padding: 0; box-sizing: border-box; }

html {
  background: var(--fm-bg);
}

body {
  font-family: 'Poppins', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: var(--fm-bg);
  color: var(--fm-text);
  font-size: 15px;
  line-height: 1.5;
  -webkit-tap-highlight-color: transparent;
  text-rendering: optimizeLegibility;
}

button,
input,
select,
textarea {
  font: inherit;
}

button {
  color: inherit;
}

::selection {
  background: rgba(30, 111, 220, 0.18);
}

h1,
h2,
h3,
h4 {
  color: var(--fm-text);
  letter-spacing: 0;
  line-height: 1.18;
}

p {
  color: var(--fm-muted);
}

/* Boutons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  border-radius: var(--fm-control-radius);
  border: 1px solid transparent;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background var(--fm-transition), border-color var(--fm-transition), color var(--fm-transition), box-shadow var(--fm-transition), transform var(--fm-transition);
  width: 100%;
  min-height: 48px;
  box-shadow: none;
}
.btn-primary {
  background: linear-gradient(180deg, #247BEA 0%, var(--fm-accent) 100%);
  color: white;
  box-shadow: 0 10px 22px rgba(30, 111, 220, 0.2);
}
.btn-primary:hover { background: linear-gradient(180deg, #1E6FDC 0%, #155FC4 100%); transform: translateY(-1px); }
.btn-primary:active { background: #155FC4; transform: scale(0.98); }
.btn-primary:disabled {
  background: #CBD5E1;
  color: #F8FAFC;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}
.btn-danger {
  background: var(--fm-danger);
  color: white;
  box-shadow: 0 10px 22px rgba(239, 68, 68, 0.16);
}
.btn-danger:hover { background: #DC2626; transform: translateY(-1px); }
.btn-secondary {
  background: var(--fm-surface);
  color: var(--fm-navy);
  border-color: var(--fm-border);
}
.btn-secondary:hover {
  background: var(--fm-surface-soft);
  border-color: #CBD5E1;
  transform: translateY(-1px);
}

/* Cards */
.card {
  background: var(--fm-surface);
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: var(--fm-card-radius);
  padding: 20px;
  box-shadow: var(--fm-shadow-subtle);
  margin-bottom: 16px;
}

/* Inputs */
.input {
  width: 100%;
  padding: 14px 16px;
  border: 1.5px solid var(--fm-border);
  border-radius: var(--fm-control-radius);
  background: var(--fm-surface);
  color: var(--fm-text);
  font-size: 16px;
  transition: border-color var(--fm-transition), box-shadow var(--fm-transition), background var(--fm-transition);
  outline: none;
  min-height: 48px;
}
.input::placeholder { color: #94A3B8; }
.input:focus {
  border-color: var(--fm-accent);
  box-shadow: 0 0 0 4px rgba(30, 111, 220, 0.12);
}

.label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--fm-muted);
  margin-bottom: 6px;
}

.error { color: var(--fm-danger); font-size: 13px; margin-top: 8px; }
.success { color: #15803D; font-size: 13px; margin-top: 8px; }

.badge {
  display: inline-block;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid transparent;
}
.badge-green { background: var(--fm-success-soft); color: #166534; border-color: #BBF7D0; }
.badge-orange { background: var(--fm-warning-soft); color: #92400E; border-color: #FDE68A; }
.badge-blue { background: var(--fm-accent-soft); color: #1D4ED8; border-color: #BFDBFE; }
.badge-gray { background: var(--fm-surface-soft); color: var(--fm-muted); border-color: var(--fm-border); }

/* Page layout */
.page {
  padding: 28px;
  max-width: 1100px;
  margin: 0 auto;
}

.page-title {
  color: var(--fm-text);
  font-weight: 800;
  letter-spacing: 0;
  margin-bottom: 18px;
}

.page-header {
  margin-bottom: 20px;
}

.table,
.table-top,
.table-heure,
table {
  color: var(--fm-text);
}

.table th,
.table-top th,
.table-heure th,
th {
  color: var(--fm-muted);
  background: #F8FAFC;
}

.table td,
.table-top td,
.table-heure td,
td {
  border-color: var(--fm-border);
}

.table tr,
.table-top tr,
.table-heure tr {
  transition: background var(--fm-transition);
}

/* Grilles responsive */
.grid-4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

/* Responsive tablette */
@media (max-width: 900px) {
  .grid-4 { grid-template-columns: repeat(2, 1fr); }
  .grid-3 { grid-template-columns: repeat(2, 1fr); }
}

/* Responsive mobile */
@media (max-width: 600px) {
  .page { padding: 16px; }
  .card { padding: 16px; border-radius: 16px; }
  .grid-4, .grid-3, .grid-2 { grid-template-columns: 1fr; }
  .btn { font-size: 16px; padding: 16px 20px; min-height: 52px; }
  .input { font-size: 16px; padding: 14px; }
}

/* ==========================================
   MODE CONTRASTE FORT — plein soleil
   ========================================== */
body.contraste-fort {
  background: #000000 !important;
  color: #ffffff !important;
}

body.contraste-fort .card {
  background: #1a1a1a !important;
  border: 2px solid #ffffff !important;
  box-shadow: none !important;
}

body.contraste-fort .navbar {
  background: #000000 !important;
  border-bottom: 3px solid #ffff00 !important;
  border-right: 3px solid #ffff00 !important;
  box-shadow: none !important;
}

body.contraste-fort .navbar-links a,
body.contraste-fort .navbar-brand {
  color: #ffffff !important;
}

body.contraste-fort .navbar-links a.router-link-active {
  background: #ffff00 !important;
  color: #000000 !important;
}

body.contraste-fort .brand-mark {
  background: #ffff00 !important;
  color: #000000 !important;
  box-shadow: none !important;
}

body.contraste-fort .nav-icon,
body.contraste-fort .nav-label,
body.contraste-fort .bn-label {
  color: currentColor !important;
}

body.contraste-fort .bottom-nav {
  background: #000000 !important;
  border-top: 3px solid #ffff00 !important;
  border-color: #ffff00 !important;
  box-shadow: none !important;
}

body.contraste-fort .bn-item {
  color: #ffffff !important;
}

body.contraste-fort .bn-item.router-link-active {
  background: #ffff00 !important;
  color: #000000 !important;
}

body.contraste-fort .bn-item.router-link-active .nav-icon,
body.contraste-fort .bn-item.router-link-active .bn-label,
body.contraste-fort .navbar-links a.router-link-active .nav-icon,
body.contraste-fort .navbar-links a.router-link-active .nav-label {
  color: #000000 !important;
}

body.contraste-fort .input {
  background: #000000 !important;
  border: 2px solid #ffffff !important;
  color: #ffffff !important;
  font-size: 18px !important;
}

body.contraste-fort .input:focus {
  border-color: #ffff00 !important;
  outline: 2px solid #ffff00 !important;
}

body.contraste-fort .label {
  color: #ffff00 !important;
  font-size: 15px !important;
  font-weight: 700 !important;
}

body.contraste-fort .btn-primary {
  background: #ffff00 !important;
  color: #000000 !important;
  border: 2px solid #ffffff !important;
  font-size: 17px !important;
  font-weight: 800 !important;
}

body.contraste-fort .btn-secondary {
  background: #333333 !important;
  color: #ffffff !important;
  border: 2px solid #ffffff !important;
  font-size: 17px !important;
}

body.contraste-fort .btn-primary:disabled {
  background: #555555 !important;
  color: #999999 !important;
}

body.contraste-fort h1,
body.contraste-fort h2,
body.contraste-fort h3,
body.contraste-fort h4 {
  color: #ffff00 !important;
}

body.contraste-fort p,
body.contraste-fort span,
body.contraste-fort div,
body.contraste-fort strong {
  color: #ffffff !important;
}

body.contraste-fort .hint {
  color: #cccccc !important;
}

body.contraste-fort .badge-green {
  background: #00aa00 !important;
  color: #ffffff !important;
}

body.contraste-fort .badge-orange {
  background: #ff8800 !important;
  color: #000000 !important;
}

body.contraste-fort .badge-blue {
  background: #0066ff !important;
  color: #ffffff !important;
}

body.contraste-fort .success {
  color: #00ff00 !important;
  font-weight: 700 !important;
}

body.contraste-fort .error {
  color: #ff4444 !important;
  font-weight: 700 !important;
}

body.contraste-fort .page-title {
  color: #ffff00 !important;
  font-size: 26px !important;
}

body.contraste-fort select {
  background: #000000 !important;
  color: #ffffff !important;
  border: 2px solid #ffffff !important;
}

body.contraste-fort table {
  border: 2px solid #ffffff !important;
}

body.contraste-fort th {
  background: #333333 !important;
  color: #ffff00 !important;
}

body.contraste-fort td {
  border-color: #555555 !important;
  color: #ffffff !important;
}

</style>
